import { ComponentProps } from 'react';

import events from '~/modules/game-events/game-events';
import { useEventListenerSelector } from '~/modules/game-events/hooks';
import useMicMonitoring from '~/modules/hooks/use-mic-monitoring';
import { LOCAL_PLAYER_NUMBERS } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import MicCheckSlot from '~/routes/sing-a-song/song-selection/components/song-settings/mic-check/mic-check-slot';
import NoiseDetection from '~/routes/sing-a-song/song-selection/components/song-settings/mic-check/noise-detection';

/**
 * One slot per player, in rows of four across the card. Nothing here says whether microphones are set up
 * at all - that is the whole empty state in `game-settings`, which stands where the Play row would
 * be rather than as an overlay on these slots.
 */
export default function MicCheck(props: ComponentProps<'div'>) {
  useMicMonitoring();

  // Every event that can change who is in a slot, not just the name: read straight from the
  // singleton during render, the row froze on whatever the roster was the first time it drew - so a
  // microphone set up after that never showed here, which is the one question these slots answer.
  //
  // Reduced to plain readings HERE rather than handed to the row as `PlayerEntity` objects. The
  // manager mutates those in place - `setName` writes to the same instance - and the React Compiler
  // memoises a row's output on the identity of what it is given, so a row holding one cached the
  // name it read the first time and never looked again. Assigning a singer in the mic setup left
  // the card still saying "Player 1" while the track pickers beside it, which read the same way
  // this now does, said the singer's name.
  const slots = useEventListenerSelector(
    [events.playerInputChanged, events.playerAdded, events.playerRemoved, events.playerNameChanged],
    () =>
      LOCAL_PLAYER_NUMBERS.map((number) => {
        const player = PlayersManager.getPlayer(number);
        // A slot on `Dummy` is one nobody has claimed - see `slot-state`.
        const connected = player !== undefined && player.input.source !== 'Dummy';

        return {
          number,
          connected,
          // A claimed slot is named after whoever is in it - a phone's name or an assigned profile;
          // every other one is "Player N", which is what `getName()` falls back to anyway.
          name: connected ? player.getName() : `Player ${number + 1}`,
        };
      }),
  );

  return (
    <div
      {...props}
      className={`typography relative grid w-full grid-cols-2 text-2xl sm:grid-cols-4 ${slots.length > 4 ? 'gap-2' : 'gap-3'} ${
        props.className ?? ''
      }`}>
      {/* `pointer-events-none`: this box is absolutely positioned across the full width of the row
          and grows UPWARDS (`bottom-full`), so while the warning is showing it sits over the
          settings above - the difficulty row and the profile rows - and swallowed their clicks.
          The warning is text with no controls in it, so nothing is lost by letting clicks through. */}
      <div className="pointer-events-none absolute right-0 bottom-full left-0 z-30">
        <NoiseDetection />
      </div>
      {slots.map((slot) => (
        <MicCheckSlot key={slot.number} playerIndex={slot.number} name={slot.name} connected={slot.connected} />
      ))}
    </div>
  );
}
